import { IChartBackendService, ISorter, RawChartDataLike, ChartConfigLike } from "./types";
export class BubbleChartBackendServiceTS {
  private readonly chartBackendService: IChartBackendService;
  private readonly sorter: ISorter;

  constructor(chartBackendService: IChartBackendService, sorter: ISorter) {
    this.chartBackendService = chartBackendService;
    this.sorter = sorter;
  }

  /**
   * Fetches raw chart data from the backend API
   * @param tableId - The table identifier
   * @param filter - Filter configuration
   * @param config - Chart configuration
   * @param isBiView - Whether this is for bi-directional view
   * @returns Promise resolving to raw chart data
   */
  async fetchJson(
    tableId: string | number,
    filter: any,
    config: any,
    isBiView?: boolean
  ): Promise<any> {
    return this.chartBackendService.fetchJson(tableId, filter, config, isBiView);
  }

  /**
   * Processes raw chart data into bubble chart visualization format
   * 
   * Bubble charts require three dimensions:
   * - X-axis: Horizontal position
   * - Y-axis: Vertical position  
   * - Size: Bubble size (value)
   * 
   * Optionally supports series for multi-series bubble charts.
   * 
   * @param resultData - Raw data from the backend
   * @param config - Chart configuration
   * @returns Processed data ready for bubble chart rendering
   */
  getRepresentation(resultData: RawChartDataLike, config: ChartConfigLike): any[] {
    let data: any[] = [];

    const seriesType = resultData.config.series
      ? resultData.config.series.data_type
      : resultData.config.key.data_type;
    
    const xAxisType = resultData.config.key.data_type;
    const yAxisType = resultData.config.y_axis.data_type;
    const valueType = resultData.config.values.data_type;

    const seriesParser =
      typeof config.chartSeries === 'object' && config.chartSeries.fieldName
        ? this.chartBackendService.getParserForType(seriesType)
        : null;
    const xAxisParser = this.chartBackendService.getParserForType(xAxisType);
    const yAxisParser = this.chartBackendService.getParserForType(yAxisType);
    const valueParser = this.chartBackendService.getParserForType(valueType);

    data = this.chartBackendService
      .nest()
      .key((d: any) => {
        return typeof config.chartSeries === 'object' && config.chartSeries.fieldName ? seriesParser!(d.series) : "";
      })
      .rollup((array: any[]) => {
        const dataPoints: any[] = [];
        
        for (let i = 0; i < array.length; i++) {
          const value = {
            x: xAxisParser(array[i].key),
            y: yAxisParser(array[i].yaxis),
            size: valueParser(array[i].values[0]),
          };
          
          if (value.x !== null && value.y !== null && value.size !== null) {
            dataPoints.push(value);
          }
        }
        
        return dataPoints.length ? dataPoints : null;
      })
      .excludeNullKeys(true)   
      .entries(resultData.values)
      .filter((item: any) => item.values !== null); 

    data = this.sorter.sort(data, {
      dataType: seriesType,
      valueParser: (item: any) => item.key,
    });

    return data;
  }
}

/**
 * Angular.js compatibility wrapper factory
 * Creates a service instance compatible with Angular's dependency injection
 * 
 * @param chartBackendService - Chart backend service instance
 * @param sorter - Sorter service instance
 * @returns Object with bound methods compatible with Angular services
 */
export function createAngularCompatibleBubbleService(
  chartBackendService: IChartBackendService,
  sorter: ISorter
): { fetchJson: Function; getRepresentation: Function } {
  const service = new BubbleChartBackendServiceTS(chartBackendService, sorter);
  return {
    fetchJson: service.fetchJson.bind(service),
    getRepresentation: service.getRepresentation.bind(service),
  };
}

